import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import PreVueExplorer from "../components/PreVueExplorer";

const LOADING_MESSAGES = [
  "Calculating population patterns",
  "Aggregating age-band data",
  "Preparing the conditions library",
  "Formulating the comparison view",
];

export default function AnalyticsPage() {
  const [census, setCensus] = useState(null);
  const [conditions, setConditions] = useState(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadAnalytics = useCallback(async () => {
    setLoadError("");
    try {
      const [censusResponse, conditionsResponse] = await Promise.all([
        api.get("/analytics/census-summary"),
        api.get("/analytics/conditions"),
      ]);
      setCensus(censusResponse.data);
      setConditions(conditionsResponse.data);
    } catch (error) {
      const message = formatApiError(error.response?.data?.detail);
      setLoadError(message || "PreVue data could not be prepared. Please try again.");
      toast.error(message || "PreVue data could not be prepared.");
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(preference.matches);
    updatePreference();
    preference.addEventListener("change", updatePreference);
    return () => preference.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const timer = window.setInterval(() => {
      setMessageIndex((index) => (index + 1) % LOADING_MESSAGES.length);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [reducedMotion]);

  if (!census || !conditions) {
    if (loadError) {
      return (
        <div className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10" data-testid="analytics-load-error">
          <div className="border flex flex-wrap items-center justify-between gap-4 px-6 py-8 text-sm" style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--muted)" }} role="alert">
            <span>{loadError}</span>
            <button type="button" className="btn-ghost !px-3 !py-2" onClick={loadAnalytics} data-testid="analytics-retry-button">
              Try again
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10" data-testid="analytics-loading">
        <div
          className="border flex items-center gap-4 px-6 py-8 text-sm"
          style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--muted)" }}
          role="status"
          aria-live="polite"
          data-testid="prevue-loading-panel"
        >
          <span
            className={`prevue-loading-spinner${reducedMotion ? " prevue-loading-spinner--reduced" : ""}`}
            aria-hidden="true"
            data-testid="prevue-loading-spinner"
          />
          <div>
            <div className="eyebrow mb-1">Preparing PreVue</div>
            <p data-testid="prevue-loading-message">{LOADING_MESSAGES[messageIndex]}…</p>
          </div>
        </div>
      </div>
    );
  }

  return <PreVueExplorer census={census} conditions={conditions.conditions} />;
}
